import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { YouTubeDetails } from '../../../services/file.service';

@Component({
  selector: 'app-youtube-embed',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="yt" [class.yt--playing]="playing">
      <ng-container *ngIf="!playing; else iframeTpl">
        <button type="button" class="yt__poster" (click)="play()" [attr.aria-label]="'Play: ' + (youtube.title || 'YouTube video')">
          <img class="yt__thumb"
               [src]="youtube.thumbnail_url"
               [alt]="youtube.title || ''"
               loading="lazy"
               decoding="async">
          <span class="yt__playOverlay" aria-hidden="true">
            <svg viewBox="0 0 68 48" width="68" height="48">
              <path class="yt__btnBg" d="M66.52 7.74A8.49 8.49 0 0 0 60.6 1.8C55.36.5 34 .5 34 .5S12.64.5 7.4 1.8A8.49 8.49 0 0 0 1.48 7.74 89.6 89.6 0 0 0 0 24a89.6 89.6 0 0 0 1.48 16.26 8.49 8.49 0 0 0 5.92 5.94C12.64 47.5 34 47.5 34 47.5s21.36 0 26.6-1.3a8.49 8.49 0 0 0 5.92-5.94A89.6 89.6 0 0 0 68 24a89.6 89.6 0 0 0-1.48-16.26z" fill="#f00"/>
              <path d="M45 24 27 14v20" fill="#fff"/>
            </svg>
          </span>
          <span class="yt__title" *ngIf="youtube.title">{{ youtube.title }}</span>
        </button>
      </ng-container>
      <ng-template #iframeTpl>
        <iframe class="yt__frame"
                [src]="safeEmbedUrl"
                title="YouTube video"
                frameborder="0"
                referrerpolicy="strict-origin-when-cross-origin"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
                allowfullscreen></iframe>
      </ng-template>
    </div>
  `,
  styles: [`
    .yt {
      position: relative;
      width: 100%;
      aspect-ratio: 16 / 9;
      background: #000;
      border-radius: var(--radius-md);
      overflow: hidden;
      box-shadow: var(--shadow-sm);
    }
    .yt__poster {
      position: absolute; inset: 0;
      display: block; width: 100%; height: 100%;
      padding: 0; border: 0; background: transparent;
      cursor: pointer;
    }
    .yt__thumb { width: 100%; height: 100%; object-fit: cover; display: block; }
    .yt__playOverlay {
      position: absolute; inset: 0;
      display: flex; align-items: center; justify-content: center;
      pointer-events: none;
      transition: transform var(--dur-base) var(--ease-out);
    }
    .yt__poster:hover .yt__playOverlay { transform: scale(1.05); }
    .yt__title {
      position: absolute; left: 0; right: 0; bottom: 0;
      padding: var(--space-3) var(--space-4);
      color: #fff;
      font-size: var(--fs-sm);
      font-weight: var(--fw-medium);
      text-align: left;
      background: linear-gradient(0deg, rgba(0,0,0,0.7), rgba(0,0,0,0));
    }
    .yt__frame { width: 100%; height: 100%; border: 0; }
  `]
})
export class YoutubeEmbedComponent {
  @Input({ required: true }) youtube!: YouTubeDetails;
  @Output() played = new EventEmitter<void>();

  private sanitizer = inject(DomSanitizer);
  playing = false;
  safeEmbedUrl: SafeResourceUrl | null = null;

  play(): void {
    if (this.playing) return;
    const url = this.youtube.embed_url + (this.youtube.embed_url.includes('?') ? '&' : '?') + 'autoplay=1';
    this.safeEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.playing = true;
    this.played.emit();
  }
}
